import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import CustomInput from 'components/CustomInput/customInput';
import CustomSelect from 'components/CustomSelect/customSelect';
import { SalesRoute } from 'config';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { updateData } from 'services/CommonServices';
import { useAppSelector } from 'store/hooks';
import { USER_RELOAD } from 'store/types/UserTypes';
import { JOBCLOSINGSTATUS } from 'utils/ArrayConstants';
import { alertOptions } from 'utils/Config';
import { MASTER_ENDPOINT } from 'utils/Constant';

interface CloseJobComponentProps {
  open: boolean;
  onClose: () => void;
  jobData: any;
}

const CloseJobComponent: React.FC<CloseJobComponentProps> = ({
  open,
  onClose,
  jobData,
}) => {
  const dispatch: any = useDispatch();
  const navigate: any = useNavigate();
  const { userInfo }: any = useAppSelector((store: any) => store.userLogin);
  const [jobClosingStatus, setJobClosingStatus] = useState<string>('filled');
  const [comments, setComments] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleClose = () => {
    setJobClosingStatus('filled');
    setComments('');
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!jobClosingStatus) {
      setError('Please select a reason');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const formData = new FormData();
      const jobsArr = [];
      const wsIds = [];

      // Add work status IDs
      jobData?.jobWorkStatus?.map((e: any) => {
        wsIds.push(e.workStatus.id);
      });

      // Add job skills
      jobData.jobSkills.map((item: any) => {
        jobsArr.push({
          preffered: item.preffered,
          required: item.required,
          rowId: item.id,
          skillId: item.skillId,
        });
      });

      // Append all form data
      formData.append('customerId', jobData?.customerId);
      formData.append('certificates', jobData?.certificates);
      formData.append('organizationId', userInfo.organizationId);
      formData.append('city', jobData?.city);
      formData.append('education', jobData?.education);
      formData.append('educationNotes', jobData?.educationNotes);
      formData.append('interviewType', jobData?.interviewType);
      formData.append('jobDurationFrom', jobData?.jobDurationFrom);
      formData.append('jobDurationTo', jobData?.jobDurationTo);
      formData.append('jobTitle', jobData?.jobTitle);
      formData.append('jobsSkills', JSON.stringify(jobsArr));
      formData.append('notes', jobData?.notes);
      formData.append('payRangeFrom', jobData?.payRangeFrom);
      formData.append('payRangeTo', jobData?.payRangeTo);
      formData.append('zip', jobData?.zip);
      formData.append('workType', jobData?.workType);
      formData.append('jobWorkStatusIds', JSON.stringify(wsIds));
      formData.append('state', jobData?.state);
      formData.append('startDate', jobData?.startDate);
      formData.append('projectRole', jobData?.projectRole);
      formData.append('positionType', jobData?.positionType);
      formData.append('payRateType', jobData?.payRateType);
      formData.append('jobDescription', jobData?.jobDescription);
      formData.append(
        'salaryDesired',
        jobData['salaryDesired'] ? jobData['salaryDesired'] : 0,
      );
      formData.append('status', jobClosingStatus);
      formData.append('publish', jobData?.publish);
      formData.append('removeRowId', JSON.stringify([]));
      formData.append('removeFileId', JSON.stringify([]));

      // Add comments if provided
      if (comments) {
        formData.append('closeJobComments', comments);
      }

      // Call API
      await updateData(jobData.id, formData, MASTER_ENDPOINT.Jobs, true);

      // Dispatch reload action
      dispatch({
        type: USER_RELOAD,
      });

      // Show success toast
      toast.success('Job closed successfully', alertOptions);

      // Close modal
      handleClose();

      // Navigate back
      navigate(SalesRoute.JOBiprisePOST);
    } catch (err: any) {
      console.error('Error closing job:', err);
      const errorMessage =
        err?.error?.data?.message ||
        err?.error?.message ||
        err?.message ||
        'Failed to close job';
      setError(errorMessage);
      toast.error(errorMessage, alertOptions);
    } finally {
      setIsLoading(false);
    }
  };

  const modalStyle = {
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    left: '50%',
    outline: 'none',
    padding: '24px',
    position: 'absolute' as const,
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="close-job-modal-title"
      aria-describedby="close-job-modal-description">
      <Box sx={modalStyle}>
        {/* Close Button */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            '&:hover': {
              backgroundColor: '#f3f4f6',
            },
            color: '#6b7280',
            position: 'absolute',
            right: 8,
            top: 8,
          }}>
          <CloseIcon />
        </IconButton>

        {/* Title */}
        <Typography
          id="close-job-modal-title"
          sx={{
            color: '#111827',
            fontFamily: 'Arial, sans-serif',
            fontSize: '20px',
            fontWeight: '700',
            mb: 2,
          }}>
          Close Job Posting
        </Typography>

        {/* Instructional Text */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              color: '#707070',
              fontFamily: 'Arial, sans-serif',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '20px',
            }}>
            Are you sure you want to close this job posting?
          </Typography>
          <Typography
            sx={{
              color: '#707070',
              fontFamily: 'Arial, sans-serif',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '20px',
            }}>
            Please select a reason for closing:
          </Typography>
        </Box>

        {/* Reason Selection */}
        <Box sx={{ mb: 1 }}>
          <CustomSelect
            onChange={(e: any) => {
              setJobClosingStatus(e?.target?.value);
              setError('');
            }}
            value={jobClosingStatus}
            labelKey={'name'}
            valueKey={'id'}
            options={JOBCLOSINGSTATUS}
            placeHolder={''}
            className={'common-selects'}
            disabled={isLoading}
          />

          {error && (
            <Typography
              sx={{
                color: '#dc2626',
                fontFamily: 'Arial, sans-serif',
                fontSize: '12px',
                mt: 0.5,
              }}>
              {error}
            </Typography>
          )}
        </Box>

        {/* Comments Text Area */}
        <Box sx={{ mb: 3, width: '100%' }}>
          <CustomInput
            isTextArea={true}
            placeHolder="Add your comments or notes here..."
            value={comments}
            onChange={(e: any) => setComments(e?.target?.value)}
            className={'common-textarea'}
            extraStyle={{ width: '100%' }}
            minRows={4}
            disabled={isLoading}
          />
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            mt: 3,
          }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              '&:disabled': {
                opacity: 0.6,
              },
              '&:hover': {
                backgroundColor: '#f9fafb',
                borderColor: '#d1d5db',
              },
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              color: '#374151',
              fontFamily: 'Arial, sans-serif',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '100px',
              padding: '8px 20px',
              textTransform: 'none',
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{
              '&:disabled': {
                backgroundColor: '#dc2626',
                opacity: 0.6,
              },
              '&:hover': {
                backgroundColor: '#b91c1c',
              },
              backgroundColor: '#dc2626',
              borderRadius: '6px',
              color: '#ffffff',
              fontFamily: 'Arial, sans-serif',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '120px',
              padding: '8px 20px',
              textTransform: 'none',
            }}>
            {isLoading ? 'Closing...' : 'Close Job'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CloseJobComponent;
